<template>
    <slot name="stone-type"></slot>

    <FilterAccordion
        v-qa="'filter-price'"
        :count="
            modelValue?.priceRange?.min?.amount &&
            modelValue?.priceRange?.max?.amount
                ? 1
                : 0
        "
        :title="t('diamond_search.filter.price.input')"
        hide-count
    >
        <slot name="price"></slot>
    </FilterAccordion>

    <FilterAccordion
        v-qa="'filter-carat-size'"
        :count="
            modelValue?.caratWeightRange?.min &&
            modelValue?.caratWeightRange?.max
                ? 1
                : 0
        "
        :title="t('diamond_search.filter.carat_size.input')"
        hide-count
    >
        <FilterRangeSegmented
            v-model="caratWeightModel"
            :segments-config="
                getCaratSegmentsConfig(
                    Number(bounds.caratWeight.min),
                    Number(bounds.caratWeight.max)
                )
            "
            :min-label="'Minimum carat weight'"
            :max-label="'Maximum carat weight'"
            :bounds="bounds.caratWeight"
            :value-formatter="formatNumericProperty"
        />
    </FilterAccordion>

    <FilterAccordion
        v-if="bounds.colors.length"
        :count="colorModel.length"
        :title="t('diamond_search.filter.colour.input')"
    >
        <FilterToggle
            v-model="colorModel"
            v-qa="'filter-colour'"
            :bounds="bounds.colors.map((c) => c.systemCode)"
            :formatter="(value: string) => value.replace(`DIAMOND_`, '')"
        />
    </FilterAccordion>
</template>

<script lang="ts" setup>
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'

import type {
    NonSkuCaratWeightRangeInput,
    NonSkuLabGrownDiamondBoundsFragment,
    NonSkuLabGrownDiamondsFilters,
    StoneColorCode,
} from '@/graphql/api'
import { getCaratSegmentsConfig } from '../input-range-config'
import { formatNumericProperty } from '@/domain/stone/labels'

import FilterToggle from '@/components/stone/search/sku/filter/FilterToggle.vue'
import FilterAccordion from '@/components/ui/filter/FilterAccordion.vue'
import FilterRangeSegmented from './filter/FilterRangeSegmented.vue'

const props = defineProps<{
    modelValue: NonSkuLabGrownDiamondsFilters | null | undefined
    bounds: NonSkuLabGrownDiamondBoundsFragment
}>()

const emit = defineEmits<{
    'update:modelValue': [v: NonSkuLabGrownDiamondsFilters]
}>()

const { t } = useI18n()

const caratWeightModel = computed({
    get(): NonSkuCaratWeightRangeInput | null | undefined {
        const values = props.modelValue

        if (!values) {
            return props.bounds.caratWeight
        }

        if (!values.caratWeightRange) {
            return props.bounds.caratWeight
        }

        return values.caratWeightRange
    },
    set(range: NonSkuCaratWeightRangeInput | null | undefined) {
        emit('update:modelValue', {
            ...props.modelValue,
            caratWeightRange: {
                max: range?.max,
                min: range?.min,
            },
        })
    },
})

const colorModel = computed({
    get(): StoneColorCode[] {
        return props.modelValue?.colors || []
    },
    set(colors: StoneColorCode[]) {
        emit('update:modelValue', {
            ...props.modelValue,
            colors: colors,
        })
    },
})
</script>
